import type { Cue } from './model/cue.ts';

export type PreprocessFlags = {
  preProcessing1: boolean;
  preProcessing2: boolean;
  /** Merge adjacent cues that share the exact same start/end times. */
  preProcessing3: boolean;
};

/** Storage slice used to derive effective preprocess flags for the current mode. */
export type PreprocessFlagSource = {
  practiceMode?: unknown;
  preProcessing1?: unknown;
  preProcessing2?: unknown;
  preProcessing3?: unknown;
  /** When practiceMode is shadowing, force preProcessing1 on without writing storage. */
  shadowingAutoRemoveLineBreaks?: unknown;
};

/**
 * Effective preprocess flags for the pipeline.
 * Shadowing + auto-remove-line-breaks OR the stored preProcessing1 — storage
 * checkboxes themselves are never mutated here.
 */
export function resolvePreprocessFlags(source: PreprocessFlagSource): PreprocessFlags {
  const mode = typeof source.practiceMode === 'string' ? source.practiceMode : '';
  const shadowing = mode === 'shadowing';
  const autoRemoveBreaks = shadowing && source.shadowingAutoRemoveLineBreaks !== false;
  return {
    preProcessing1: Boolean(source.preProcessing1) || autoRemoveBreaks,
    preProcessing2: Boolean(source.preProcessing2),
    preProcessing3: Boolean(source.preProcessing3),
  };
}

export type PreprocessedCue = Cue & { preprocessed?: boolean };

function stripDialogueDashes(text: string): string {
  return text
    .replace(/^-/g, '')
    .replace(/<br>-/g, '<br>')
    .replace(/\n-/g, '<br>')
    .replace(/-$/g, '');
}

/**
 * Collapse line breaks to spaces while keeping speaker dashes.
 * e.g. "Hey<br>-You?" → "Hey -You?" so dialogue stays readable.
 */
function mergeLineBreaksPreserveDashes(text: string): string {
  return text
    .replace(/(?:<br\s*\/?>|\r\n|\n|\r)-/gi, ' -')
    .replace(/(?:<br\s*\/?>|\r\n|\n|\r)/gi, ' ');
}

/** Match leading `<br>` / newlines (with optional surrounding whitespace). */
const LEADING_BREAK_RE = /^(?:(?:\s|&nbsp;)*(?:<br\s*\/?>|\r\n|\n|\r)+)+/gi;

/**
 * After bracket removal, drop break markers left at the start
 * (e.g. `[note]<br>Hello` → `<br>Hello` → `Hello`).
 */
function stripLeadingBreaks(text: string): string {
  return text.replace(LEADING_BREAK_RE, '');
}

/**
 * Join consecutive text cues with identical timing
 * (e.g. SRT 1804+1805 both 00:58:38,834 → 00:58:44,208).
 */
export function mergeSameTimingAdjacent(cues: PreprocessedCue[]): PreprocessedCue[] {
  const out: PreprocessedCue[] = [];
  for (const cue of cues) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.start === cue.start &&
      prev.end === cue.end &&
      !prev.src &&
      !cue.src
    ) {
      const left = (prev.text || '').trim();
      const right = (cue.text || '').trim();
      if (!right) continue;
      prev.text = left ? `${left} ${right}` : right;
      prev.segs = [...(prev.segs ?? []), ...(cue.segs ?? [])];
      prev.preprocessed = true;
      continue;
    }
    out.push({
      ...cue,
      segs: [...(cue.segs ?? [])],
      preprocessed: true,
    });
  }
  return out;
}

/** Mirrors legacy preprocessor.js process() (B-PRE-02/03), with dialogue dashes kept on merge. */
export function preprocessCues(parsed: Cue[], flags: PreprocessFlags): PreprocessedCue[] {
  const arr: PreprocessedCue[] = [];

  for (let i = 0; i < parsed.length; i += 1) {
    const source = parsed[i];
    if (!source) continue;

    const obj: PreprocessedCue = { ...source, preprocessed: true };
    let text = obj.text;

    if (flags.preProcessing1) {
      // 「移除換行」：合併行，但保留對話指示 `-`（用戶回報必要）。
      text = mergeLineBreaksPreserveDashes(text);
    } else if (flags.preProcessing2) {
      // 僅「移除括號」時沿用舊行為：清掉對話 dash。
      text = stripDialogueDashes(text);
    }

    if (flags.preProcessing2) {
      text = text.replace(/\(.*?\)|\[.*?\]|\{.*?\}|（.*?）|【.*?】|《.*?》/gs, '');
      text = stripLeadingBreaks(text);
    }

    text = text.replace(/\s+/g, ' ').trim();
    if (text.length === 0) continue;

    obj.text = text;
    arr.push(obj);
  }

  if (flags.preProcessing3) {
    return mergeSameTimingAdjacent(arr);
  }
  return arr;
}

/**
 * Mirrors preprocessor.js main() branch (B-PRE-01/04).
 * When all switches are off, returns the original reference unchanged.
 */
export function applyPreprocessorBranch<T extends Cue>(
  originalRef: T[],
  flags: PreprocessFlags,
  processFn: (cues: T[]) => PreprocessedCue[],
): T[] | PreprocessedCue[] {
  if (!flags.preProcessing1 && !flags.preProcessing2 && !flags.preProcessing3) {
    return originalRef;
  }
  return processFn(originalRef);
}

/** Image tracks skip preprocessing in legacy main loop (B-PRE-04). */
export function shouldSkipPreprocessForTrackType(trackType: string | undefined): boolean {
  return trackType === 'image';
}
